/**
 * Liquidity Panel — idra.padigital.id/liquid
 *
 * Public, wallet-optional browse of all IDRA/USDC liquidity locked in the
 * IdraLiquidityLocker contract(s), with release dates, plus a wallet-gated form to
 * add + lock new liquidity and (once matured) withdraw it.
 *
 * Reads come straight from each locker's on-chain view functions via a read-only
 * JsonRpcProvider (no backend/indexer). Writes go through the connected wallet using
 * the same eth_sendTransaction pattern as AdminPanel.
 */

import { h, Fragment } from 'preact';
import { useState, useEffect, useMemo } from 'preact/hooks';
import { Contract, JsonRpcProvider, formatUnits, parseUnits } from 'ethers';
import { modal } from './WalletConnect';
import { LOCKERS, LockerConfig } from '../config/lockers';

const LOCKER_ABI = [
  'function idra() view returns (address)',
  'function usdc() view returns (address)',
  'function pair() view returns (address)',
  'function treasuryA() view returns (address)',
  'function treasuryB() view returns (address)',
  'function totalLocks() view returns (uint256)',
  'function getLocks(uint256 offset, uint256 limit) view returns (tuple(address depositor, uint128 lpAmount, uint128 idraAmount, uint128 usdcAmount, uint64 lockedAt, uint64 unlockTime, bool withdrawn)[])',
  'function addAndLock(uint256 idraAmount, uint256 usdcAmount, uint256 minIdra, uint256 minUsdc, uint64 unlockTime) returns (uint256)',
  'function withdraw(uint256 id)',
  'function withdrawAndRemove(uint256 id, uint256 minIdra, uint256 minUsdc) returns (uint256, uint256)',
];

const ERC20_ABI = [
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
  'function approve(address spender, uint256 amount) returns (bool)',
];

const PAIR_ABI = [
  'function getReserves() view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)',
  'function token0() view returns (address)',
  'function totalSupply() view returns (uint256)',
];

const BACKEND_URL = (import.meta.env.VITE_SWAP_BACKEND_URL as string) || 'https://idra.padigital.id';

// Warn when the entered ratio deviates from the reference rate by more than this.
const DEVIATION_WARN_PCT = 2;

const PAGE = 100; // locks fetched per RPC call
const MAX_LOCKS = 1000; // safety cap; if exceeded we surface a notice (no silent truncation)

interface Meta {
  idra: string;
  usdc: string;
  pair: string;
  idraDecimals: number;
  usdcDecimals: number;
  idraSymbol: string;
  usdcSymbol: string;
  treasuryA: string;
  treasuryB: string;
}

interface LockRow {
  id: number;
  depositor: string;
  lpAmount: bigint;
  idraAmount: bigint;
  usdcAmount: bigint;
  lockedAt: number;
  unlockTime: number;
  withdrawn: boolean;
}

interface LiquidityPanelProps {
  walletAddress: string | null;
  chainId: number | null;
}

export function LiquidityPanel({ walletAddress, chainId }: LiquidityPanelProps) {
  // Single-deployment MVP: use the first configured locker.
  const config: LockerConfig | undefined = LOCKERS[0];

  const [meta, setMeta] = useState<Meta | null>(null);
  const [rows, setRows] = useState<LockRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>('');

  const [idraInput, setIdraInput] = useState('');
  const [usdcInput, setUsdcInput] = useState('');
  const [unlockDate, setUnlockDate] = useState('');
  const [slippagePct, setSlippagePct] = useState('1');

  // Reference rate = IDRA per 1 USDC. Sourced from the live pool reserves when the
  // pool has liquidity, otherwise from the IdraSwap oracle. `linked` keeps the two
  // amount inputs matched to it so you can't accidentally seed an off-ratio pool.
  const [rate, setRate] = useState<number | null>(null);
  const [rateSource, setRateSource] = useState<'pool' | 'oracle' | ''>('');
  const [poolHasLiquidity, setPoolHasLiquidity] = useState(false);
  const [linked, setLinked] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [statusError, setStatusError] = useState(false);

  const rpc = useMemo(
    () => (config ? new JsonRpcProvider(config.rpcUrl) : null),
    [config?.rpcUrl]
  );

  useEffect(() => {
    if (config && rpc) loadAll();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.lockerAddress]);

  async function loadAll() {
    if (!config || !rpc) return;
    try {
      setLoading(true);
      setLoadError('');
      const locker = new Contract(config.lockerAddress, LOCKER_ABI, rpc);

      const [idra, usdc, pair, treasuryA, treasuryB, totalBn] = await Promise.all([
        locker.idra(),
        locker.usdc(),
        locker.pair(),
        locker.treasuryA(),
        locker.treasuryB(),
        locker.totalLocks(),
      ]);

      const idraC = new Contract(idra, ERC20_ABI, rpc);
      const usdcC = new Contract(usdc, ERC20_ABI, rpc);
      const [idraDecimals, usdcDecimals, idraSymbol, usdcSymbol] = await Promise.all([
        idraC.decimals(),
        usdcC.decimals(),
        idraC.symbol().catch(() => 'IDRA'),
        usdcC.symbol().catch(() => 'USDC'),
      ]);

      setMeta({
        idra,
        usdc,
        pair,
        idraDecimals: Number(idraDecimals),
        usdcDecimals: Number(usdcDecimals),
        idraSymbol,
        usdcSymbol,
        treasuryA,
        treasuryB,
      });

      // Determine the reference rate (IDRA per USDC): prefer live pool reserves,
      // fall back to the IdraSwap oracle when the pool is still empty.
      await resolveRate(pair, idra, Number(idraDecimals), Number(usdcDecimals));

      const total = Number(totalBn);
      const cap = Math.min(total, MAX_LOCKS);
      setTruncated(total > MAX_LOCKS);

      const collected: LockRow[] = [];
      for (let offset = 0; offset < cap; offset += PAGE) {
        const page = await locker.getLocks(offset, PAGE);
        page.forEach((l: any, i: number) => {
          collected.push({
            id: offset + i,
            depositor: l.depositor,
            lpAmount: l.lpAmount,
            idraAmount: l.idraAmount,
            usdcAmount: l.usdcAmount,
            lockedAt: Number(l.lockedAt),
            unlockTime: Number(l.unlockTime),
            withdrawn: l.withdrawn,
          });
        });
      }
      // Newest first.
      collected.reverse();
      setRows(collected);
    } catch (e: any) {
      console.error('Failed to load locks:', e);
      setLoadError(e?.message || 'Failed to load liquidity locks.');
    } finally {
      setLoading(false);
    }
  }

  /** Resolve IDRA-per-USDC rate from pool reserves (authoritative) or the oracle. */
  async function resolveRate(
    pairAddr: string,
    idraAddr: string,
    idraDecimals: number,
    usdcDecimals: number
  ) {
    if (!rpc) return;
    // 1) Live pool reserves — the true on-chain price once liquidity exists.
    try {
      const pairC = new Contract(pairAddr, PAIR_ABI, rpc);
      const [r, token0] = await Promise.all([pairC.getReserves(), pairC.token0()]);
      const idraIsToken0 = token0.toLowerCase() === idraAddr.toLowerCase();
      const reserveIdra: bigint = idraIsToken0 ? r.reserve0 : r.reserve1;
      const reserveUsdc: bigint = idraIsToken0 ? r.reserve1 : r.reserve0;
      if (reserveIdra > 0n && reserveUsdc > 0n) {
        const idraHuman = Number(formatUnits(reserveIdra, idraDecimals));
        const usdcHuman = Number(formatUnits(reserveUsdc, usdcDecimals));
        setRate(idraHuman / usdcHuman);
        setRateSource('pool');
        setPoolHasLiquidity(true);
        return;
      }
    } catch (e) {
      console.warn('getReserves failed:', e);
    }
    // 2) Empty pool — fall back to the IdraSwap oracle (IDRA per USDC).
    setPoolHasLiquidity(false);
    try {
      const res = await fetch(`${BACKEND_URL}/swap/rate`);
      if (res.ok) {
        const data = await res.json();
        if (data?.rate) {
          setRate(Number(data.rate));
          setRateSource('oracle');
          return;
        }
      }
    } catch (e) {
      console.warn('oracle rate fetch failed:', e);
    }
    setRate(null);
    setRateSource('');
  }

  /** Two-way linked inputs: keep IDRA/USDC matched to the reference rate. */
  function onIdraInput(v: string) {
    setIdraInput(v);
    if (linked && rate && meta && v && !isNaN(parseFloat(v))) {
      setUsdcInput((parseFloat(v) / rate).toFixed(meta.usdcDecimals));
    }
  }
  function onUsdcInput(v: string) {
    setUsdcInput(v);
    if (linked && rate && v && !isNaN(parseFloat(v))) {
      setIdraInput((parseFloat(v) * rate).toFixed(2));
    }
  }

  /**
   * Parse a <input type="datetime-local"> value to epoch seconds in the USER'S
   * LOCAL timezone. We build the Date from explicit components instead of
   * `new Date(string)` because bare "YYYY-MM-DDTHH:mm" string parsing is
   * engine-dependent (some browsers treat it as UTC or return Invalid Date),
   * which was making near-future times read as invalid/past.
   */
  function parseUnlockToEpoch(s: string): number | null {
    const m = s && s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
    let d: Date;
    if (m) {
      d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], m[6] ? +m[6] : 0);
    } else {
      d = new Date(s); // fallback for browsers without native datetime-local
    }
    const t = d.getTime();
    return Number.isFinite(t) ? Math.floor(t / 1000) : null;
  }

  // Implied price from the current inputs, and its deviation from the reference.
  const impliedRate =
    idraInput && usdcInput && parseFloat(usdcInput) > 0
      ? parseFloat(idraInput) / parseFloat(usdcInput)
      : null;
  const deviationPct =
    impliedRate && rate ? ((impliedRate - rate) / rate) * 100 : null;
  const offRatio = deviationPct !== null && Math.abs(deviationPct) > DEVIATION_WARN_PCT;

  /** The EIP-1193 provider we send through (prefer the injected wallet). */
  function walletProvider(): any {
    const w3m = modal.getWalletProvider() as any;
    const winEth = (window as any).ethereum;
    return winEth && winEth !== w3m ? winEth : w3m;
  }

  /**
   * Force the wallet onto the locker's chain (Polygon) before any tx. Switches
   * via wallet_switchEthereumChain, adding the network if the wallet doesn't have
   * it. Returns true once the wallet is on the right chain.
   */
  async function ensureChain(): Promise<boolean> {
    if (!config) return false;
    const prov = walletProvider();
    if (!prov?.request) throw new Error('Wallet not available');
    const hexId = '0x' + config.chainId.toString(16);
    try {
      const current = await prov.request({ method: 'eth_chainId' });
      if (parseInt(current, 16) === config.chainId) return true;
      await prov.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hexId }],
      });
      return true;
    } catch (e: any) {
      // 4902 = chain not added to the wallet → add it, then it's selected.
      if (e?.code === 4902 && config.chainId === 137) {
        await prov.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: hexId,
              chainName: 'Polygon Mainnet',
              nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
              rpcUrls: ['https://polygon-bor-rpc.publicnode.com', 'https://polygon-rpc.com'],
              blockExplorerUrls: ['https://polygonscan.com'],
            },
          ],
        });
        return true;
      }
      throw e;
    }
  }

  /** Send a tx via the connected wallet and wait for 1 confirmation. */
  async function sendTx(to: string, data: string): Promise<string> {
    if (!config) throw new Error('No locker configured');
    const sendProvider = walletProvider();
    if (!sendProvider?.request) throw new Error('Wallet not available');

    const txHash: string = await sendProvider.request({
      method: 'eth_sendTransaction',
      params: [{ from: walletAddress!.toLowerCase(), to, data }],
    });
    const provider = new JsonRpcProvider(config.rpcUrl);
    await provider.waitForTransaction(txHash, 1, 180000);
    return txHash;
  }

  const canWithdraw = (row: LockRow): boolean => {
    if (!walletAddress || row.withdrawn) return false;
    if (Date.now() / 1000 < row.unlockTime) return false;
    const me = walletAddress.toLowerCase();
    return (
      me === row.depositor.toLowerCase() ||
      (!!meta && me === meta.treasuryA.toLowerCase()) ||
      (!!meta && me === meta.treasuryB.toLowerCase())
    );
  };

  async function handleAddAndLock() {
    if (!config || !meta || !walletAddress) return;
    if (!idraInput || !usdcInput || !unlockDate) {
      setStatusError(true);
      setStatus('Enter IDRA amount, USDC amount and an unlock date.');
      return;
    }
    const unlockTs = parseUnlockToEpoch(unlockDate);
    if (unlockTs === null) {
      setStatusError(true);
      setStatus('Pick a valid unlock date & time.');
      return;
    }
    // Require a small buffer so a barely-future time can't revert on-chain by the
    // time the tx mines (block.timestamp must be strictly < unlockTime).
    const nowSec = Math.floor(Date.now() / 1000);
    if (unlockTs <= nowSec + 60) {
      const picked = new Date(unlockTs * 1000).toLocaleString(undefined, {
        timeZoneName: 'short',
      });
      setStatusError(true);
      setStatus(
        unlockTs <= nowSec
          ? `That unlock date is in the past — you picked ${picked}. Double-check the month/day (${localTz}).`
          : `Unlock must be at least ~1 min ahead — you picked ${picked} (${localTz}).`
      );
      return;
    }

    try {
      setBusy(true);
      setStatusError(false);

      setStatus(`Switching wallet to ${config.chainLabel}…`);
      await ensureChain();

      const idraAmount = parseUnits(idraInput, meta.idraDecimals);
      const usdcAmount = parseUnits(usdcInput, meta.usdcDecimals);
      const slip = BigInt(Math.round(parseFloat(slippagePct || '0') * 100)); // bps
      const minIdra = idraAmount - (idraAmount * slip) / 10_000n;
      const minUsdc = usdcAmount - (usdcAmount * slip) / 10_000n;

      const erc20 = new Contract(meta.idra, ERC20_ABI).interface;
      const lockerIface = new Contract(config.lockerAddress, LOCKER_ABI).interface;

      setStatus(`Approving ${meta.idraSymbol}… confirm in your wallet`);
      await sendTx(meta.idra, erc20.encodeFunctionData('approve', [config.lockerAddress, idraAmount]));

      setStatus(`Approving ${meta.usdcSymbol}… confirm in your wallet`);
      await sendTx(meta.usdc, erc20.encodeFunctionData('approve', [config.lockerAddress, usdcAmount]));

      setStatus('Adding & locking liquidity… confirm in your wallet');
      const txHash = await sendTx(
        config.lockerAddress,
        lockerIface.encodeFunctionData('addAndLock', [
          idraAmount,
          usdcAmount,
          minIdra,
          minUsdc,
          unlockTs,
        ])
      );

      setStatus(`Liquidity locked! Tx ${txHash.slice(0, 10)}…`);
      setIdraInput('');
      setUsdcInput('');
      setUnlockDate('');
      await loadAll();
    } catch (e: any) {
      console.error('addAndLock failed:', e);
      setStatusError(true);
      setStatus(
        e?.code === 'ACTION_REJECTED' || e?.code === 4001
          ? 'Transaction cancelled.'
          : `Failed: ${e?.shortMessage || e?.message || 'unknown error'}`
      );
    } finally {
      setBusy(false);
    }
  }

  /** Withdraw + burn the LP on the DEX, returning IDRA + USDC in one tx. */
  async function handleRedeem(row: LockRow) {
    if (!config || !meta || !rpc) return;
    try {
      setBusy(true);
      setStatusError(false);
      setStatus(`Switching wallet to ${config.chainLabel}…`);
      await ensureChain();

      // Slippage minimums from the lock's proportional share of current reserves.
      const pairC = new Contract(meta.pair, PAIR_ABI, rpc);
      const [res, token0, supply] = await Promise.all([
        pairC.getReserves(),
        pairC.token0(),
        pairC.totalSupply(),
      ]);
      const idraIsToken0 = token0.toLowerCase() === meta.idra.toLowerCase();
      const reserveIdra: bigint = idraIsToken0 ? res.reserve0 : res.reserve1;
      const reserveUsdc: bigint = idraIsToken0 ? res.reserve1 : res.reserve0;
      const lp = row.lpAmount;
      const slip = BigInt(Math.round(parseFloat(slippagePct || '1') * 100));
      const expIdra = supply > 0n ? (reserveIdra * lp) / supply : 0n;
      const expUsdc = supply > 0n ? (reserveUsdc * lp) / supply : 0n;
      const minIdra = expIdra - (expIdra * slip) / 10_000n;
      const minUsdc = expUsdc - (expUsdc * slip) / 10_000n;

      setStatus(`Redeeming lock #${row.id} to IDRA + USDC… confirm in your wallet`);
      const lockerIface = new Contract(config.lockerAddress, LOCKER_ABI).interface;
      const txHash = await sendTx(
        config.lockerAddress,
        lockerIface.encodeFunctionData('withdrawAndRemove', [row.id, minIdra, minUsdc])
      );
      setStatus(`Redeemed! IDRA + USDC sent to your wallet. Tx ${txHash.slice(0, 10)}…`);
      await loadAll();
    } catch (e: any) {
      console.error('withdrawAndRemove failed:', e);
      setStatusError(true);
      setStatus(
        e?.code === 'ACTION_REJECTED' || e?.code === 4001
          ? 'Transaction cancelled.'
          : `Failed: ${e?.shortMessage || e?.message || 'unknown error'}`
      );
    } finally {
      setBusy(false);
    }
  }

  /** Withdraw the raw LP token only (advanced — keeps the pool position). */
  async function handleWithdraw(row: LockRow) {
    if (!config) return;
    try {
      setBusy(true);
      setStatusError(false);
      setStatus(`Switching wallet to ${config.chainLabel}…`);
      await ensureChain();
      setStatus(`Withdrawing LP for lock #${row.id}… confirm in your wallet`);
      const lockerIface = new Contract(config.lockerAddress, LOCKER_ABI).interface;
      const txHash = await sendTx(
        config.lockerAddress,
        lockerIface.encodeFunctionData('withdraw', [row.id])
      );
      setStatus(`Withdrawn! LP token sent to your wallet. Tx ${txHash.slice(0, 10)}…`);
      await loadAll();
    } catch (e: any) {
      console.error('withdraw failed:', e);
      setStatusError(true);
      setStatus(
        e?.code === 'ACTION_REJECTED' || e?.code === 4001
          ? 'Transaction cancelled.'
          : `Failed: ${e?.shortMessage || e?.message || 'unknown error'}`
      );
    } finally {
      setBusy(false);
    }
  }

  // ---- render helpers ----
  const localTz =
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time'
      : 'local time';
  const unlockEpoch = parseUnlockToEpoch(unlockDate);
  // "now" as a datetime-local value (local tz) for the input's `min`.
  const nowLocalInput = (() => {
    const n = new Date();
    const p = (x: number) => String(x).padStart(2, '0');
    return `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}T${p(n.getHours())}:${p(n.getMinutes())}`;
  })();
  const fmt = (v: bigint, decimals: number) =>
    parseFloat(formatUnits(v, decimals)).toLocaleString(undefined, {
      maximumFractionDigits: decimals,
    });
  const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;
  const dt = (ts: number) => (ts ? new Date(ts * 1000).toLocaleString() : '—');

  const statusOf = (row: LockRow) => {
    if (row.withdrawn) return { label: 'Withdrawn', cls: 'bg-gray-100 text-gray-600' };
    if (Date.now() / 1000 >= row.unlockTime)
      return { label: 'Unlockable', cls: 'bg-green-100 text-green-700' };
    return { label: 'Locked', cls: 'bg-blue-100 text-blue-700' };
  };

  if (!config) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center bg-yellow-50 border border-yellow-200 rounded-lg">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Liquidity Locks</h1>
        <p className="text-yellow-800">
          No liquidity locker is configured yet. Set{' '}
          <code className="font-mono">VITE_IDRA_LIQUIDITY_LOCKER_MAINNET</code> once the
          contract is deployed.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">IDRA Liquidity Locks</h1>
        <p className="mt-2 text-gray-600">
          IDRA/USDC liquidity on {config.dexLabel} ({config.chainLabel}) — locked, with
          public release dates.
        </p>
        {meta && (
          <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm">
            <a
              className="text-blue-600 hover:underline"
              href={config.addLiquidityUrl(meta.idra, meta.usdc)}
              target="_blank"
              rel="noreferrer"
            >
              Add liquidity on {config.dexLabel} ↗
            </a>
            <a
              className="text-blue-600 hover:underline"
              href={config.removeLiquidityUrl(meta.idra, meta.usdc)}
              target="_blank"
              rel="noreferrer"
              title="Hold LP tokens from a past 'LP only' withdrawal? On QuickSwap: filter v2 → Import → paste the pair → Remove to get IDRA + USDC back."
            >
              Manage / remove LP on {config.dexLabel} ↗
            </a>
            <a
              className="text-blue-600 hover:underline"
              href={config.swapUrl(meta.idra, meta.usdc)}
              target="_blank"
              rel="noreferrer"
            >
              Trade IDRA on {config.dexLabel} ↗
            </a>
            {meta.pair && (
              <a
                className="text-blue-600 hover:underline"
                href={`${config.explorerUrl}/address/${meta.pair}`}
                target="_blank"
                rel="noreferrer"
              >
                LP pair {short(meta.pair)} ↗
              </a>
            )}
          </div>
        )}
      </div>

      {/* Add & lock */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">Add &amp; lock liquidity</h2>
        {!walletAddress ? (
          <p className="text-gray-600">Connect your wallet to add and lock liquidity.</p>
        ) : (
          <Fragment>
            {/* Reference rate + empty-pool price warning */}
            {rate && (
              <div
                className={`mb-4 p-3 rounded-lg text-sm border ${
                  poolHasLiquidity
                    ? 'bg-blue-50 border-blue-200 text-blue-800'
                    : 'bg-amber-50 border-amber-300 text-amber-900'
                }`}
              >
                <div className="font-medium">
                  Reference rate: 1 {meta?.usdcSymbol || 'USDC'} ={' '}
                  {rate.toLocaleString(undefined, { maximumFractionDigits: 2 })}{' '}
                  {meta?.idraSymbol || 'IDRA'}{' '}
                  <span className="font-normal opacity-80">
                    ({rateSource === 'pool' ? 'from pool reserves' : 'from IDRA oracle'})
                  </span>
                </div>
                {!poolHasLiquidity && (
                  <div className="mt-1">
                    ⚠️ This pool is <strong>empty</strong> — the ratio you provide sets
                    IDRA's <strong>initial price</strong>. It's pre-filled from the current
                    rate; seeding off-rate lets arbitrageurs take the difference.
                  </div>
                )}
                <label className="mt-2 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={linked}
                    onChange={(e) => setLinked((e.target as HTMLInputElement).checked)}
                  />
                  <span>Match amounts to this rate automatically</span>
                </label>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {meta?.idraSymbol || 'IDRA'} amount
                </label>
                <input
                  type="number"
                  value={idraInput}
                  onInput={(e) => onIdraInput((e.target as HTMLInputElement).value)}
                  placeholder="0.00"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {meta?.usdcSymbol || 'USDC'} amount
                </label>
                <input
                  type="number"
                  value={usdcInput}
                  onInput={(e) => onUsdcInput((e.target as HTMLInputElement).value)}
                  placeholder="0.00"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Locked until
                </label>
                <input
                  type="datetime-local"
                  value={unlockDate}
                  min={nowLocalInput}
                  onInput={(e) => setUnlockDate((e.target as HTMLInputElement).value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <p
                  className={`mt-1 text-xs ${
                    unlockEpoch && unlockEpoch <= Math.floor(Date.now() / 1000)
                      ? 'text-red-600 font-medium'
                      : 'text-gray-500'
                  }`}
                >
                  Your local time ({localTz})
                  {unlockEpoch && (
                    <>
                      {' '}
                      → unlocks{' '}
                      {new Date(unlockEpoch * 1000).toLocaleString(undefined, {
                        timeZoneName: 'short',
                      })}
                      {unlockEpoch <= Math.floor(Date.now() / 1000) && ' — that date is in the past!'}
                    </>
                  )}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slippage tolerance (%)
                </label>
                <input
                  type="number"
                  value={slippagePct}
                  onInput={(e) => setSlippagePct((e.target as HTMLInputElement).value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            {impliedRate !== null && (
              <div
                className={`mt-3 text-sm ${offRatio ? 'text-red-700 font-medium' : 'text-gray-600'}`}
              >
                Your ratio: 1 {meta?.usdcSymbol || 'USDC'} ={' '}
                {impliedRate.toLocaleString(undefined, { maximumFractionDigits: 2 })}{' '}
                {meta?.idraSymbol || 'IDRA'}
                {deviationPct !== null && (
                  <span>
                    {' '}
                    ({deviationPct >= 0 ? '+' : ''}
                    {deviationPct.toFixed(2)}% vs reference)
                  </span>
                )}
                {offRatio && (
                  <div className="mt-1">
                    ⚠️ This is more than {DEVIATION_WARN_PCT}% off the reference rate.
                    {poolHasLiquidity
                      ? ' The pool will only use the on-ratio portion and refund the rest.'
                      : " On an empty pool you'd be mispricing IDRA — fix the amounts or re-enable matching."}
                  </div>
                )}
              </div>
            )}
            <button
              onClick={handleAddAndLock}
              disabled={busy}
              className={`mt-4 w-full py-3 text-white rounded-lg font-semibold transition disabled:opacity-50 ${
                offRatio ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:bg-blue-600'
              }`}
            >
              {busy ? 'Processing…' : offRatio ? 'Add & lock anyway' : 'Add & lock'}
            </button>
            <p className="mt-2 text-xs text-gray-500">
              Approvals for both tokens are requested first, then the add+lock. Unused
              tokens (from the pool ratio) are refunded to you automatically.
            </p>
          </Fragment>
        )}
        {status && (
          <div
            className={`mt-4 p-3 rounded-lg text-sm ${
              statusError
                ? 'bg-red-50 text-red-700 border border-red-200'
                : 'bg-blue-50 text-blue-700 border border-blue-200'
            }`}
          >
            {status}
          </div>
        )}
      </div>

      {/* Browse */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-700">Locked positions</h2>
            <p className="text-xs text-gray-500">Dates shown in your local time ({localTz})</p>
          </div>
          <button
            onClick={loadAll}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="py-12 text-center">
            <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-3 text-gray-600">Loading locks…</p>
          </div>
        ) : loadError ? (
          <p className="text-red-600">{loadError}</p>
        ) : rows.length === 0 ? (
          <p className="text-gray-600">No liquidity has been locked yet.</p>
        ) : (
          <Fragment>
            {truncated && (
              <p className="mb-3 text-xs text-amber-700">
                Showing the most recent {MAX_LOCKS} locks; older locks are not displayed.
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 text-left text-gray-600">
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">{meta?.idraSymbol || 'IDRA'}</th>
                    <th className="px-3 py-2">{meta?.usdcSymbol || 'USDC'}</th>
                    <th className="px-3 py-2">LP</th>
                    <th className="px-3 py-2">Depositor</th>
                    <th className="px-3 py-2">Locked on</th>
                    <th className="px-3 py-2">Unlocks on</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const s = statusOf(row);
                    return (
                      <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-500">{row.id}</td>
                        <td className="px-3 py-2">{fmt(row.idraAmount, meta!.idraDecimals)}</td>
                        <td className="px-3 py-2">{fmt(row.usdcAmount, meta!.usdcDecimals)}</td>
                        <td className="px-3 py-2">{fmt(row.lpAmount, 18)}</td>
                        <td className="px-3 py-2 font-mono">
                          <a
                            href={`${config.explorerUrl}/address/${row.depositor}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {short(row.depositor)}
                          </a>
                        </td>
                        <td className="px-3 py-2 text-gray-500">{dt(row.lockedAt)}</td>
                        <td className="px-3 py-2 font-medium">{dt(row.unlockTime)}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.cls}`}>
                            {s.label}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {canWithdraw(row) && (
                            <div className="flex flex-col items-start gap-1">
                              <button
                                onClick={() => handleRedeem(row)}
                                disabled={busy}
                                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium disabled:opacity-50"
                                title="Burns the LP and returns your IDRA + USDC in one transaction"
                              >
                                Withdraw → IDRA + USDC
                              </button>
                              <button
                                onClick={() => handleWithdraw(row)}
                                disabled={busy}
                                className="text-[11px] text-gray-500 hover:text-gray-700 underline disabled:opacity-50"
                                title="Advanced: receive the raw LP token and keep the pool position"
                              >
                                LP token only
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
}
